import React, { useState, useEffect } from 'react';
import { ChefHat, Award, Globe, Heart, Users, Instagram, Twitter, Linkedin } from 'lucide-react';
import { mockData } from '../mock';
import { formHandlers, trackPageView } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';

export const About = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Track page view on component mount
  useEffect(() => {
    trackPageView('/about');
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;

    setIsSubmitting(true);
    try {
      const result = await formHandlers.subscribeToNewsletter(newsletterEmail);
      if (result.success) {
        toast({
          title: "Success!",
          description: result.message,
        });
        setNewsletterEmail('');
      } else {
        toast({
          title: "Error",
          description: result.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const socialIcons = {
    Instagram: Instagram,
    Twitter: Twitter,
    Linkedin: Linkedin
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="section-padding">
        <div className="container">
          <div className="grid-two-column">
            <div>
              <h1 className="hero-large" style={{ marginBottom: '32px' }}>
                Your Luxury Hospitality Partner
              </h1>
              <p className="body-large" style={{ marginBottom: '32px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                Welcome to Decadent Palete, where we provide high-end luxury hospitality experiences from large to small events, weddings, travel groups, retreats, and elite celebrations reaching unparalleled sophistication. 
                We don't just cook — we provide unforgettable moments and services that transform your vision into extraordinary experiences. 
                Every detail is meticulously curated to exceed your expectations.
              </p>
              
              <div style={{ marginBottom: '40px' }}>
                <h3 className="heading-3" style={{ marginBottom: '16px' }}>Culinary Philosophy</h3>
                <p className="body-regular" style={{ 
                  color: 'var(--text-secondary)',
                  fontStyle: 'italic',
                  fontSize: '18px',
                  lineHeight: '1.6'
                }}>
                  "{mockData.chef.philosophy}"
                </p>
              </div>
              
              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                <Button className="btn-primary">
                  View Our Services
                </Button>
                <Button variant="ghost" className="btn-secondary">
                  Plan Your Event
                </Button>
              </div>
            </div>
            
            <div style={{ position: 'relative' }}>
              <img
                src={mockData.foodShowcase[0].url}
                alt="Executive Chef at work"
                style={{
                  width: '100%',
                  height: '500px',
                  objectFit: 'cover',
                  borderRadius: '0px'
                }}
              />
              <div style={{
                position: 'absolute',
                bottom: '24px',
                left: '24px',
                background: 'rgba(0, 0, 0, 0.8)',
                color: 'white',
                padding: '16px 24px',
                borderRadius: '0px'
              }}>
                <p className="body-regular" style={{ margin: 0 }}>
                  {mockData.chef.experience} of Culinary Excellence
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chef Specialties */}
      <section style={{ background: 'var(--bg-secondary)', padding: '80px 0' }}>
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '80px' }}>
            Culinary Expertise
          </h2>
          
          <div className="grid-three-column">
            {mockData.chef.specialties.map((specialty, index) => {
              const icons = [Globe, ChefHat, Award, Heart];
              const IconComponent = icons[index] || ChefHat;
              const colors = ['var(--color-fence-green)', 'var(--color-gold)', 'var(--color-fence-green)', 'var(--color-gold)'];
              
              return (
                <Card key={index} className="hover-lift" style={{
                  border: '1px solid var(--border-light)',
                  borderRadius: '0px',
                  background: 'var(--bg-primary)',
                  padding: '40px 32px',
                  textAlign: 'center'
                }}>
                  <CardHeader>
                    <div style={{
                      width: '80px',
                      height: '80px',
                      background: colors[index],
                      borderRadius: '0px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 24px auto'
                    }}>
                      <IconComponent size={32} color="white" />
                    </div>
                    <CardTitle className="heading-3">{specialty}</CardTitle>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Signature Dishes Gallery */}
      <section className="section-padding">
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '80px' }}>
            Chef's Signature Creations
          </h2>
          
          <div className="grid-three-column">
            {mockData.chefSignatureCreations.map((dish) => (
              <div key={dish.id} className="hover-lift" style={{ cursor: 'pointer' }}>
                <img
                  src={dish.url}
                  alt={dish.alt}
                  style={{
                    width: '100%',
                    height: '300px',
                    objectFit: 'cover',
                    marginBottom: '16px',
                    borderRadius: '0px'
                  }}
                />
                <h3 className="heading-3" style={{ marginBottom: '8px' }}>{dish.title}</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  {dish.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DP Gift Cards */}
      <section style={{ background: 'var(--color-fence-green)', padding: '80px 0', color: 'white' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: 'var(--color-gold)',
              borderRadius: '0px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px auto'
            }}>
              <Heart size={32} color="white" />
            </div>
            <h2 className="hero-medium" style={{ marginBottom: '24px', color: 'white' }}>
              {mockData.giftCards.title}
            </h2>
            <p className="body-large" style={{ 
              color: 'rgba(255, 255, 255, 0.95)', 
              maxWidth: '600px', 
              margin: '0 auto 16px auto' 
            }}>
              {mockData.giftCards.subtitle}
            </p>
            <p className="body-regular" style={{ 
              color: 'rgba(255, 255, 255, 0.8)', 
              maxWidth: '700px', 
              margin: '0 auto' 
            }}>
              {mockData.giftCards.description}
            </p>
          </div>

          {/* Gift Card Amounts */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '20px',
            maxWidth: '1000px',
            margin: '0 auto 60px auto'
          }}>
            {mockData.giftCards.amounts.map((amount, index) => (
              <div key={index} style={{
                background: amount.popular ? 'var(--color-gold)' : 'rgba(255, 255, 255, 0.1)',
                border: amount.popular ? '2px solid var(--color-gold)' : '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '0px',
                padding: '32px 24px',
                textAlign: 'center',
                position: 'relative',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              className="hover-lift"
              onMouseEnter={(e) => {
                if (!amount.popular) {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                }
              }}
              onMouseLeave={(e) => {
                if (!amount.popular) {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                }
              }}>
                {amount.popular && (
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'var(--color-fence-green)',
                    color: 'white',
                    padding: '6px 16px',
                    fontSize: '11px',
                    fontWeight: '600',
                    textTransform: 'uppercase'
                  }}>
                    Most Popular
                  </div>
                )}
                <div className="hero-medium" style={{ 
                  color: amount.popular ? 'white' : 'white',
                  marginBottom: '8px'
                }}>
                  ${amount.value}
                </div>
                <p className="body-small" style={{ 
                  color: amount.popular ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.7)',
                  margin: 0
                }}>
                  Gift Card
                </p>
              </div>
            ))}
          </div>

          {/* Custom Amount */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            padding: '40px',
            borderRadius: '0px',
            textAlign: 'center',
            maxWidth: '500px',
            margin: '0 auto 60px auto'
          }}>
            <h3 className="heading-3" style={{ marginBottom: '16px', color: 'white' }}>
              Custom Amount
            </h3>
            <p className="body-regular" style={{ 
              color: 'rgba(255, 255, 255, 0.8)',
              marginBottom: '24px'
            }}>
              Choose any amount between ${mockData.giftCards.customAmountRange.min} - ${mockData.giftCards.customAmountRange.max.toLocaleString()}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'center' }}>
              <span style={{ color: 'white', fontSize: '18px' }}>$</span>
              <Input
                type="number"
                placeholder="500"
                min={mockData.giftCards.customAmountRange.min}
                max={mockData.giftCards.customAmountRange.max}
                style={{
                  borderRadius: '0px',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  maxWidth: '120px',
                  textAlign: 'center'
                }}
              />
            </div>
          </div>

          {/* Gift Card Features */}
          <div className="grid-two-column" style={{ marginBottom: '60px' }}>
            <div>
              <h4 className="heading-3" style={{ marginBottom: '20px', color: 'white' }}>
                Gift Card Features
              </h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {mockData.giftCards.features.map((feature, index) => (
                  <li key={index} className="body-regular" style={{ 
                    marginBottom: '12px',
                    paddingLeft: '24px',
                    position: 'relative',
                    color: 'rgba(255, 255, 255, 0.9)'
                  }}>
                    <span style={{
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '8px',
                      height: '8px',
                      background: 'var(--color-gold)',
                      borderRadius: '50%'
                    }}></span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="heading-3" style={{ marginBottom: '20px', color: 'white' }}>
                Perfect For
              </h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {mockData.giftCards.benefits.map((benefit, index) => (
                  <li key={index} className="body-regular" style={{ 
                    marginBottom: '12px',
                    paddingLeft: '24px',
                    position: 'relative',
                    color: 'rgba(255, 255, 255, 0.9)'
                  }}>
                    <span style={{
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '8px',
                      height: '8px',
                      background: 'var(--color-gold)',
                      borderRadius: '50%'
                    }}></span>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Purchase Buttons */}
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button className="btn-gold">
                Purchase Digital Gift Card
              </Button>
              <Button 
                style={{
                  background: 'transparent',
                  color: 'white',
                  border: '1px solid white',
                  borderRadius: '0px',
                  padding: '19px 23px',
                  minWidth: '210px',
                  height: '60px'
                }}
              >
                Order Physical Gift Card
              </Button>
            </div>
            <p className="body-small" style={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              marginTop: '20px'
            }}>
              Digital cards delivered instantly via email. Physical cards shipped within 2-3 business days.
            </p>
          </div>
        </div>
      </section>

      {/* Social Media & Newsletter */}
      <section style={{ background: 'var(--color-lavender)', padding: '80px 0' }}>
        <div className="container">
          <div className="grid-two-column" style={{ alignItems: 'center' }}>
            <div>
              <h2 className="heading-1" style={{ marginBottom: '24px' }}>
                Follow Our Culinary Journey
              </h2>
              <p className="body-large" style={{ 
                marginBottom: '40px', 
                color: 'var(--text-secondary)' 
              }}>
                Stay connected with us on social media for behind-the-scenes content, 
                culinary tips, seasonal menu updates, and exclusive event previews.
              </p>
              
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                {mockData.socialMedia.map((social) => {
                  const IconComponent = socialIcons[social.icon];
                  return (
                    <a
                      key={social.platform}
                      href={social.url}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '16px 24px',
                        border: '1px solid var(--border-medium)',
                        borderRadius: '0px',
                        color: 'var(--text-primary)',
                        textDecoration: 'none',
                        transition: 'all 0.2s ease',
                        background: 'var(--bg-primary)'
                      }}
                      className="hover-lift"
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = 'var(--color-gold)';
                        e.target.style.background = 'var(--color-gold)';
                        e.target.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = 'var(--border-medium)';
                        e.target.style.background = 'var(--bg-primary)';
                        e.target.style.color = 'var(--text-primary)';
                      }}
                    >
                      <IconComponent size={20} />
                      <span className="body-regular">{social.platform}</span>
                    </a>
                  );
                })}
              </div>
            </div>
            
            <Card style={{
              border: '1px solid var(--border-light)',
              borderRadius: '0px',
              background: 'var(--bg-primary)',
              padding: '40px'
            }}>
              <CardHeader>
                <CardTitle className="heading-2">Subscribe & Earn Rewards</CardTitle>
                <CardDescription className="body-regular">
                  Join our newsletter for exclusive recipes, culinary tips, event invitations, 
                  and special member discounts on our services.
                </CardDescription>
              </CardHeader>
              
              <CardContent>
                <form onSubmit={handleNewsletterSubmit} style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: '16px' 
                }}>
                  <Input
                    type="email"
                    placeholder="Enter your email address"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    required
                    style={{ 
                      borderRadius: '0px',
                      border: '1px solid var(--border-medium)'
                    }}
                  />
                  <Button 
                    type="submit" 
                    className="btn-gold"
                    disabled={isSubmitting}
                    style={{ width: '100%' }}
                  >
                    {isSubmitting ? 'Subscribing...' : 'Subscribe for Rewards'}
                  </Button>
                </form>
                
                <div style={{ marginTop: '24px', textAlign: 'center' }}>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    Subscribers get early access to pop-up events and exclusive seasonal menus
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Partnership & Affiliate Program */}
      <section style={{ background: 'var(--bg-secondary)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 className="heading-1" style={{ marginBottom: '24px' }}>
              {mockData.partnershipProgram.title}
            </h2>
            <p className="body-large" style={{ 
              color: 'var(--text-secondary)', 
              maxWidth: '700px', 
              margin: '0 auto 16px auto' 
            }}>
              {mockData.partnershipProgram.subtitle}
            </p>
            <p className="body-regular" style={{ 
              color: 'var(--text-secondary)', 
              maxWidth: '800px', 
              margin: '0 auto' 
            }}>
              {mockData.partnershipProgram.description}
            </p>
          </div>

          {/* Partnership Programs */}
          <div className="grid-three-column" style={{ marginBottom: '80px' }}>
            {mockData.partnershipProgram.programs.map((program, index) => (
              <Card key={index} className="hover-lift" style={{
                border: '1px solid var(--border-light)',
                borderRadius: '0px',
                background: 'var(--bg-primary)',
                padding: '32px 24px',
                height: '100%'
              }}>
                <CardHeader>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: index === 0 ? 'var(--color-gold)' : index === 1 ? 'var(--color-fence-green)' : 'var(--color-gold)',
                    borderRadius: '0px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px auto'
                  }}>
                    <Users size={24} color="white" />
                  </div>
                  <div style={{
                    background: index === 0 ? 'var(--color-gold)' : index === 1 ? 'var(--color-fence-green)' : 'var(--color-gold)',
                    color: 'white',
                    padding: '4px 12px',
                    fontSize: '11px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    textAlign: 'center',
                    marginBottom: '16px'
                  }}>
                    {program.type}
                  </div>
                  <CardTitle className="heading-3" style={{ textAlign: 'center', marginBottom: '8px' }}>
                    {program.title}
                  </CardTitle>
                  <CardDescription className="body-small" style={{ textAlign: 'center', marginBottom: '20px' }}>
                    {program.description}
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  <div style={{ marginBottom: '20px' }}>
                    <h4 className="body-regular" style={{ 
                      fontWeight: '600', 
                      marginBottom: '12px',
                      color: 'var(--color-fence-green)'
                    }}>
                      Partner Benefits:
                    </h4>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {program.benefits.map((benefit, idx) => (
                        <li key={idx} className="body-small" style={{ 
                          marginBottom: '6px',
                          paddingLeft: '16px',
                          position: 'relative'
                        }}>
                          <span style={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '4px',
                            height: '4px',
                            background: 'var(--color-gold)',
                            borderRadius: '50%'
                          }}></span>
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="body-regular" style={{ 
                      fontWeight: '600', 
                      marginBottom: '12px',
                      color: 'var(--text-secondary)'
                    }}>
                      Requirements:
                    </h4>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {program.requirements.map((requirement, idx) => (
                        <li key={idx} className="body-small" style={{ 
                          marginBottom: '6px',
                          paddingLeft: '16px',
                          position: 'relative',
                          color: 'var(--text-secondary)'
                        }}>
                          <span style={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '4px',
                            height: '4px',
                            background: 'var(--text-secondary)',
                            borderRadius: '50%'
                          }}></span>
                          {requirement}
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Application Process */}
          <div style={{ 
            background: 'var(--color-lavender)', 
            padding: '60px 40px', 
            borderRadius: '0px',
            marginBottom: '60px'
          }}>
            <h3 className="heading-2" style={{ textAlign: 'center', marginBottom: '40px' }}>
              Partnership Application Process
            </h3>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '24px',
              maxWidth: '1000px',
              margin: '0 auto'
            }}>
              {mockData.partnershipProgram.applicationProcess.map((step, index) => (
                <div key={index} style={{ textAlign: 'center' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: 'var(--color-fence-green)',
                    color: 'white',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 12px auto',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}>
                    {index + 1}
                  </div>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    {step}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Partnership Contact */}
          <div style={{
            background: 'var(--color-fence-green)',
            color: 'white',
            padding: '60px 40px',
            textAlign: 'center'
          }}>
            <h3 className="heading-2" style={{ marginBottom: '24px', color: 'white' }}>
              Ready to Partner With Us?
            </h3>
            <p className="body-large" style={{ 
              marginBottom: '32px',
              color: 'rgba(255, 255, 255, 0.9)',
              maxWidth: '600px',
              margin: '0 auto 32px auto'
            }}>
              Join our network of successful partners and start earning with Decadent Palete. 
              Our dedicated partnership team is here to support your success.
            </p>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
              gap: '24px',
              maxWidth: '600px',
              margin: '0 auto 40px auto'
            }}>
              <div>
                <div className="body-regular" style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '8px' }}>
                  Partnership Email:
                </div>
                <a href={`mailto:${mockData.partnershipProgram.contactInfo.email}`} 
                   className="body-regular" style={{ color: 'var(--color-gold)', textDecoration: 'none' }}>
                  {mockData.partnershipProgram.contactInfo.email}
                </a>
              </div>
              <div>
                <div className="body-regular" style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '8px' }}>
                  Direct Line:
                </div>
                <a href={`tel:${mockData.partnershipProgram.contactInfo.phone.replace(/[^\d]/g, '')}`} 
                   className="body-regular" style={{ color: 'var(--color-gold)', textDecoration: 'none' }}>
                  {mockData.partnershipProgram.contactInfo.phone}
                </a>
              </div>
            </div>
            
            <div style={{ marginBottom: '40px' }}>
              <div className="body-regular" style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '8px' }}>
                Partnership Manager:
              </div>
              <div className="body-regular" style={{ color: 'var(--color-gold)' }}>
                {mockData.partnershipProgram.contactInfo.dedicatedManager}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button className="btn-gold">
                Apply for Partnership
              </Button>
              <Button 
                style={{
                  background: 'transparent',
                  color: 'white',
                  border: '1px solid white',
                  borderRadius: '0px',
                  padding: '19px 23px',
                  minWidth: '210px',
                  height: '60px'
                }}
              >
                Download Partnership Kit
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section-padding-small">
        <div className="container">
          <div style={{ 
            textAlign: 'center',
            background: 'var(--bg-secondary)',
            padding: '80px 40px',
            borderRadius: '0px'
          }}>
            <ChefHat size={64} color="var(--color-gold)" style={{ margin: '0 auto 32px auto' }} />
            <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
              Ready to Create Something Extraordinary?
            </h2>
            <p className="body-large" style={{ 
              marginBottom: '40px', 
              color: 'var(--text-secondary)',
              maxWidth: '600px',
              margin: '0 auto 40px auto'
            }}>
              Let's collaborate to design a culinary experience that exceeds your expectations 
              and creates lasting memories for you and your guests.
            </p>
            
            <div style={{ marginBottom: '40px' }}>
              <p className="body-regular" style={{ 
                marginBottom: '16px', 
                color: 'var(--text-primary)',
                fontWeight: '600'
              }}>
                Ready to plan your event? Contact us directly:
              </p>
              <p className="body-large" style={{ 
                marginBottom: '8px', 
                color: 'var(--color-gold)'
              }}>
                <a href="tel:9736663705" style={{ color: 'var(--color-gold)', textDecoration: 'none' }}>
                  (973) 666-3705
                </a>
              </p>
              <p className="body-regular" style={{ 
                color: 'var(--text-secondary)'
              }}>
                <a href="mailto:info@decadentpalete.com" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>
                  info@decadentpalete.com
                </a>
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button className="btn-gold">
                Start Planning Your Event
              </Button>
              <Button variant="ghost" className="btn-secondary">
                Explore Our Services
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};